import type { TFunction } from 'i18next';
import { CheckCircle, Icon, Lightning, MagnifyingGlass, Package, UserSquare } from '@phosphor-icons/react';

import Yup from '@tmh-website/services/validation.service';
import type { LocationProviderResponse } from '@tmh-website/services/electricity-tariff/electricity-tariff.service';

// These step enum needs to be numeric, not a string enum(!) so we can simply add to it to go to the next step
export enum Step {
  PowerConsumption,
  TariffInfo,
  UserData,
  Review,
  Thanks,
}

export enum Salutation {
  Herr = 'Herr',
  Frau = 'Frau',
}

export enum SalutationFR {
  Herr = 'Monsieur',
  Frau = 'Madame',
}

export const Title = ['', 'Dr.', 'Prof.', 'Prof. Dr.', 'Dr. Med.', 'Prof. Dr. Med.'] as const;

export enum Reason {
  Switch = 'Switch',
  Move = 'Move',
}

export enum Termination {
  TMH = 'TMH',
  Self = 'Self',
}

export enum PaymentMethod {
  DirectDebit = 'DirectDebit',
  BankTransfer = 'BankTransfer',
}

export const PaymentMethodLabels: { [key in PaymentMethod]: string } = {
  [PaymentMethod.DirectDebit]: 'Bankeinzug',
  [PaymentMethod.BankTransfer]: 'Überweisung',
};

/**
 * Reasons for cancellation, values taken from GETEC's internal system
 */
export enum CancellationReason {
  // Umzug (id 15)
  CustomerRelocation = 'CUSTOMER_RELOCATION',
  // Unzufriedenheit mit Produkt (id 30)
  CustomerProduct = 'CUSTOMER_PRODUCT',
  // Unzufriedenheit mit Preis (id 31)
  CustomerPrice = 'CUSTOMER_PRICE',
  // Unzufriedenheit mit Service (id 32)
  CustomerService = 'CUSTOMER_SERVICE',
  // sonstige Gründe / keine Angabe (id 33)
  CustomerMisc = 'CUSTOMER_MISC',
  // Preisanpassung (id 34)
  CustomerPriceChange = 'CUSTOMER_PRICE_CHANGE',
  // Preisanpassung
}

export const CancellationReasonLabels: { [key in CancellationReason]: string } = {
  [CancellationReason.CustomerRelocation]: 'Umzug',
  [CancellationReason.CustomerProduct]: 'Produkt wird nicht mehr benötigt',
  [CancellationReason.CustomerPrice]: 'Nicht zufrieden mit Preis/Angebot',
  [CancellationReason.CustomerService]: 'Service nicht zufriedenstellend',
  [CancellationReason.CustomerMisc]: 'Keine Angabe',
  [CancellationReason.CustomerPriceChange]: 'Preisanpassung',
};

export enum CancellationType {
  Ordinary = 'Ordinary',
  Exceptional = 'Exceptional',
}

export const CancellationTypeToReasonMap: { [key in CancellationType]: CancellationReason[] } = {
  [CancellationType.Ordinary]: [
    CancellationReason.CustomerProduct,
    CancellationReason.CustomerPrice,
    CancellationReason.CustomerService,
    CancellationReason.CustomerMisc,
  ],
  [CancellationType.Exceptional]: [CancellationReason.CustomerRelocation, CancellationReason.CustomerPriceChange],
};

export enum TerminationDateType {
  AsSoonAsPossible = 'AsSoonAsPossible',
  OnDate = 'OnDate',
}

export enum ETFormVariant {
  Mercedes = 'Mercedes',
  RenaultDE = 'RenaultDE',
  RenaultFR = 'RenaultFR',
  TMH = 'TMH',
}

export enum ETFormLayout {
  DE = 'DE',
  FR = 'FR',
}

/**
 * The states of an user journey in the MobilizeFR onboarding. Useful for debugging
 */
export enum SignupStates {
  /**
   * Entry is created for the first time
   */
  CREATED = 'CREATED',
  /**
   * Cannot fetch PDL information for some reason. This is likely due to the issue with triPica or wrong PDL number
   */
  FAILED_PDL_INFO_FETCH = 'FAILED_PDL_INFO_FETCH',
  /**
   * Fetched PDL info and proceeded to the next step with tariff offer.
   */
  SHOULD_RECEIVE_TARIFF_OFFER = 'ASKED_FOR_TARIFF_OFFER',
  /**
   * Consumer wanted to view the offer, but we didn't show it for some reason - this is expected
   */
  NO_TARIFF_OFFER_FOR_CUSTOMER = 'NO_TARIFF_OFFER_FOR_CUSTOMER',
  /**
   * Consumer tried to view the offer, which they should probably receive, but something went wrong
   */
  FAILED_TARIFF_OFFER_FETCH = 'FAILED_TARIFF_OFFER_FETCH',
  /**
   * Consumer chosen the tariff and proceeded to the next step
   */
  TARIFF_CHOSEN = 'TARIFF_CHOSEN',
  /**
   * Consumer tried to post the product order, but something went wrong
   */
  FAILED_PRODUCT_ORDER = 'FAILED_PRODUCT_ORDER',
  /**
   * Filled up personal data and ordered the product (in the backend it means that call to the post product order has taken place)
   */
  ORDERED = 'ORDERED',
  /**
   * Consumer tried to use validation code, but something went wrong
   */
  FAILED_SIGN_UP = 'FAILED_SIGN_UP',
  /**
   * Completed the onboarding flow by inserted correct validation code at the very end
   */
  SIGNED = 'SIGNED',
}

const WIDGET_FRESHWORKS_URL_PREFIX = `https://euc-widget.freshworks.com/`;
export const WIDGET_FRESHWORKS_URL_SUFIX = `widgetBase/bootstrap.js`;
export const WIDGET_FRESHWORKS_ID = 103000006992;
export const WIDGET_FRESHWORKS_URL = `${WIDGET_FRESHWORKS_URL_PREFIX}${WIDGET_FRESHWORKS_URL_SUFIX}`;

export const localStreetSuggestionsCache = new Map<string, string[] | Promise<string[]>>();

export const localLocationProviderCache = new Map<
  string,
  LocationProviderResponse | Promise<LocationProviderResponse>
>();

export const stepIcons: { [key in Step]: Icon } = {
  [Step.PowerConsumption]: Lightning,
  [Step.TariffInfo]: MagnifyingGlass,
  [Step.UserData]: UserSquare,
  [Step.Review]: Package,
  [Step.Thanks]: CheckCircle,
};

export const stepNames: { [key in Step]: string } = {
  [Step.PowerConsumption]: 'blocks:formConsumptionStepNames.powerConsumption',
  [Step.TariffInfo]: 'blocks:formConsumptionStepNames.tariffInfo',
  [Step.UserData]: 'blocks:formConsumptionStepNames.userData',
  [Step.Review]: 'blocks:formConsumptionStepNames.review',
  [Step.Thanks]: 'blocks:formConsumptionStepNames.thanks',
};

export const stepTrackingEvents: { [key in Step]: string } = {
  [Step.PowerConsumption]: 'ams_step_consumption',
  [Step.TariffInfo]: 'ams_step_tariff',
  [Step.UserData]: 'ams_step_personal',
  [Step.Review]: 'ams_step_overview',
  [Step.Thanks]: '',
};

export const ConfirmAccount = Yup.object({
  token: Yup.string().required(),
});

export const getEmailDoiFlowValidation = (t: TFunction) => {
  return Yup.object().shape({
    firstName: Yup.string(),
    lastName: Yup.string(),
    email: Yup.string().email().required(),
    acceptAdvertising: Yup.boolean(),
    phone: Yup.string().matches(/^\+?[0-9]+$/, t('validation:mixed.incorrectFormat')),
  });
};

export const localCitySuggestionsCache = new Map<string, string[] | Promise<string[]>>();

export const COUNTRY_CODE_TO_IBAN_PATTERN_MAP = {
  // fist two chars is country code, N - digit, A - letter, X - digit or letter
  AD: /^AD\d{2}\d{4}\d{4}[0-9A-Z]{12}$/, // ADNNNNNNNNNNXXXXXXXXXXXX
  AE: /^AE\d{2}\d{3}\d{16}$/, // AENNNNNNNNNNNNNNNNNN
  AL: /^AL\d{2}\d{8}[0-9A-Z]{16}$/, // ALNNNNNNNNXXXXXXXXXXXXXXX
  AT: /^AT\d{2}\d{5}\d{11}$/, // ATNNNNNNNNNNNNNNNNN
  AZ: /^AZ\d{2}[A-Z]{4}[0-9A-Z]{20}$/, // AZNNAAAAXXXXXXXXXXXXXXXXXXXX
  BA: /^BA\d{2}\d{6}\d{10}$/, // BANNNNNNNNNNNNNNNNN
  BE: /^BE\d{2}\d{3}\d{9}$/, // BENNNNNNNNNNNNN
  BG: /^BG\d{2}[A-Z]{4}\d{6}[0-9A-Z]{8}$/, // BGNNAAAANNNNNNXXXXXXXX
  BH: /^BH\d{2}[A-Z]{4}[0-9A-Z]{14}$/, // BHNNAAAAXXXXXXXXXXXXXX
  BI: /^BI\d{2}\d{4}\d{20}$/, // BINNNNNNNNNNNNNNNNNNNNNN
  BR: /^BR\d{2}\d{8}\d{16}[A-Z]\d$/, // BRNNNNNNNNNNNNNNNNNNNNNNNNNAX
  BY: /^BY\d{2}[0-9A-Z]{4}\d{20}$/, // BYNNXXXXNNNNXXXXXXXXXXXXXX
  CH: /^CH\d{2}\d{5}[0-9A-Z]{12}$/, // CHNNNNNNNNXXXXXXXXXXXX
  CR: /^CR\d{2}\d{4}\d{14}$/, // CRNNNNNNNNNNNNNNNNNN
  CY: /^CY\d{2}\d{4}\d{20}$/, // CYNNNNNNNNXXXXXXXXXXXXXX
  CZ: /^CZ\d{2}\d{20}$/, // CZNNNNNNNNNNNNNNNNNNNNNN
  DE: /^DE\d{2}\d{18}$/, // DENNNNNNNNNNNNNNNNNNNNN
  DJ: /^DJ\d{2}\d{4}\d{20}$/, // DJNNNNNNNNNNNNNNNNNNNNNNNN
  DK: /^DK\d{2}\d{14}$/, // DKNNNNNNNNNNNNNNNN
  DO: /^DO\d{2}[0-9A-Z]{4}\d{20}$/, // DONNXXXXNNNNNNNNNNNNNNNNNN
  EE: /^EE\d{2}\d{16}$/, // EENNNNNNNNNNNNNNNNNNN
  EG: /^EG\d{2}\d{25}$/, // EGNNNNNNNNNNNNNNNNNNNNNNNNNNN
  ES: /^ES\d{2}\d{20}$/, // ESNNNNNNNNNNNNNNNNNNNNNNNN
  FI: /^FI\d{2}\d{14}$/, // FINNNNNNNNNNNNNNNNN
  FK: /^FK\d{2}[A-Z]{2}\d{2}\d{4}\d{4}\d{2}$/, // FKNNAANNNNNNNNNNNN
  FO: /^FO\d{2}\d{14}$/, // FONNNNNNNNNNNNNNNNN
  FR: /^FR\d{2}\d{10}[0-9A-Z]{11}\d{2}$/, // FRNNNNNNNNNNXXXXXXXXXXXNN
  GB: /^GB\d{2}[A-Z]{4}\d{14}$/, // GBNNAAAANNNNNNNNNNNNN
  GE: /^GE\d{2}[A-Z]{2}\d{16}$/, // GENNAANNNNNNNNNNNNNNN
  GI: /^GI\d{2}[A-Z]{4}[0-9A-Z]{15}$/, // GINNAAAAXXXXXXXXXXXXX
  GL: /^GL\d{2}\d{14}$/, // GLNNNNNNNNNNNNNNNN
  GR: /^GR\d{2}\d{7}[0-9A-Z]{16}$/, // GRNNNNNNNXXXXXXXXXXXXXXX
  GT: /^GT\d{2}[0-9A-Z]{4}\d{20}$/, // GTNNXXXXNNNNNNNNNNNNNNNNNN
  HR: /^HR\d{2}\d{17}$/, // HRNNNNNNNNNNNNNNNNNN
  HU: /^HU\d{2}\d{24}$/, // HUNNNNNNNNNNNNNNNNNNNNNNNNN
  IE: /^IE\d{2}[A-Z]{4}\d{14}$/, // IENNAAAANNNNNNNNNNNNN
  IL: /^IL\d{2}\d{3}\d{19}$/, // ILNNNNNNNNNNNNNNNNNNNNNN
  IQ: /^IQ\d{2}[A-Z]{4}\d{15}$/, // IQNNAAAANNNNNNNNNNNNNNN
  IS: /^IS\d{2}\d{22}$/, // ISNNNNNNNNNNNNNNNNNNNNNNNN
  IT: /^IT\d{2}[A-Z]\d{10}[0-9A-Z]{12}$/, // ITNNANNNNNNNXXXXXXXXXXXX
  JO: /^JO\d{2}[A-Z]{4}\d{22}$/, // JONNAAAANNNNXXXXXXXXXXXXXX
  KW: /^KW\d{2}[A-Z]{4}[0-9A-Z]{22}$/, // KWNNAAAAXXXXXXXXXXXXXXXXXXXX
  KZ: /^KZ\d{2}\d{3}[0-9A-Z]{13}$/, // KZNNNNNXXXXXXXXXXXXX
  LB: /^LB\d{2}\d{4}[0-9A-Z]{20}$/, // LBNNNNNNXXXXXXXXXXXXXXXXXXXX
  LC: /^LC\d{2}[A-Z]{4}[0-9A-Z]{24}$/, // LCNNAAAAXXXXXXXXXXXXXXXXXXXXXXXX
  LI: /^LI\d{2}\d{5}[0-9A-Z]{12}$/, // LINNNNNNXXXXXXXXXXXX
  LT: /^LT\d{2}\d{16}$/, // LTNNNNNNNNNNNNNNNNNN
  LU: /^LU\d{2}\d{3}[0-9A-Z]{13}$/, // LUNNNNXXXXXXXXXXXXX
  LV: /^LV\d{2}[A-Z]{4}[0-9A-Z]{13}$/, // LVNNAAAAXXXXXXXXXXXXX
  LY: /^LY\d{2}\d{4}\d{4}\d{4}\d{4}\d{4}\d{1}$/, // LYNNNNNNNNNNNNNNNNNNNNNNN
  MC: /^MC\d{2}\d{10}[0-9A-Z]{11}\d{2}$/, // MCNNNNNNNNNNXXXXXXXXXXXNN
  MD: /^MD\d{2}[0-9A-Z]{20}$/, // MDNNXXXXXXXXXXXXXXXXXXXX
  ME: /^ME\d{2}\d{18}$/, // MENNNNNNNNNNNNNNNNNNN
  MK: /^MK\d{2}\d{3}[0-9A-Z]{10}\d{2}$/, // MKNNNNNXXXXXXXXXNN
  MN: /^MN\d{2}\d{3}[0-9A-Z]{13}$/, // MNNNNNXXXXXXXXXXXXX
  MR: /^MR\d{2}\d{23}$/, // MRNNNNNNNNNNNNNNNNNNNNNNNN
  MT: /^MT\d{2}[A-Z]{4}\d{5}[0-9A-Z]{18}$/, // MTNNAAAANNNNXXXXXXXXXXXXXXX
  MU: /^MU\d{2}[A-Z]{4}\d{19}[A-Z]{3}$/, // MUNNAAAANNNNNNNNNNNNNNNNNAXXAA
  NI: /^NI\d{2}\d{17}$/, // NINNAAAAANNNNNNNNNNNNNNNNNNN
  NL: /^NL\d{2}[A-Z]{4}\d{10}$/, // NLNNAAAANNNNNNNNNNN
  NO: /^NO\d{2}\d{11}$/, // NONNNNNNNNNNNN
  OM: /^OM\d{2}[A-Z]{4}\d{21}$/, // OMNNAAAANNNNNNNNNNNNNNNNNNN
  PK: /^PK\d{2}[A-Z]{4}[0-9A-Z]{16}$/, // PKNNAAAAXXXXXXXXXXXXXXXX
  PL: /^PL\d{2}\d{24}$/, // PLNNNNNNNNNNNNNNNNNNNNNNNNNN
  PS: /^PS\d{2}[A-Z]{4}\d{21}$/, // PSNNAAAAXXXXXXXXXXXXXXXXXXX
  PT: /^PT\d{2}\d{21}$/, // PTNNNNNNNNNNNNNNNNNNNNNNN
  QA: /^QA\d{2}[A-Z]{4}[0-9A-Z]{21}$/, // QANNAAAAXXXXXXXXXXXXXXXXXXX
  RO: /^RO\d{2}[A-Z]{4}[0-9A-Z]{16}$/, // RONNAAAAXXXXXXXXXXXXXXXX
  RS: /^RS\d{2}\d{18}$/, // RSNNNNNNNNNNNNNNNNNNNN
  RU: /^RU\d{2}[0-9A-Z]{30}$/, // RUNNNNNNNNNNNNNNNXXXXXXXXXXXXXXX
  SA: /^SA\d{2}\d{2}[0-9A-Z]{18}$/, // SANNNNXXXXXXXXXXXXXXXXXX
  SC: /^SC\d{2}[A-Z]{4}\d{20}[A-Z]{3}$/, // SCNNAAAANNNNNNNNNNNNNNNNNNNAAAXXX
  SD: /^SD\d{2}\d{18}$/, // SDNNNNNNNNNNNNNNNNNN
  SE: /^SE\d{2}\d{20}$/, // SENNNNNNNNNNNNNNNNNNNNN
  SI: /^SI\d{2}\d{17}$/, // SINNNNNNNNNNNNNNNNNN
  SK: /^SK\d{2}\d{20}$/, // SKNNNNNNNNNNNNNNNNNNNNNN
  SM: /^SM\d{2}[A-Z]\d{10}[0-9A-Z]{12}$/, // SMNNANNNNNNNXXXXXXXXXXXX
  SO: /^SO\d{2}[A-Z]{4}\d{21}$/, // SONNAAAANNNNNNNNNNNNNNNN
  ST: /^ST\d{2}\d{4}\d{19}$/, // STNNNNNNNNNNNNNNNNNNNNNNN
  SV: /^SV\d{2}[0-9A-Z]{4}\d{20}$/, // SVNNAAAANNNNNNNNNNNNNNNNNNN
  TL: /^TL\d{2}\d{3}\d{14}\d{2}$/, // TLNNNNNNNNNNNNNNNNNNNNN
  TN: /^TN\d{2}\d{20}$/, // TNNNNNNNNNNNNNNNNNNNNNNN
  TR: /^TR\d{2}\d{5}[0-9A-Z]{17}$/, // TRNNNNNNNNXXXXXXXXXXXXXXXXX
  UA: /^UA\d{2}\d{6}[0-9A-Z]{19}$/, // UANNNNNNXXXXXXXXXXXXXXXXXXX
  VA: /^VA\d{2}\d{18}$/, // VANNNNNNNNNNNNNNNNNNNN
  VG: /^VG\d{2}[A-Z]{4}\d{16}$/, // VGNNAAAANNNNNNNNNNNNN
  XK: /^XK\d{2}\d{4}\d{10}\d{2}$/, // XKNNNNNNNNNNNNNNNNNNN
} as const;
